import type { FilledContentRelationshipField, LinkResolverFunction } from "@prismicio/client"

import type { MakeOptional } from "@bounce/util"

import { getValidContentRelationship } from "./contentRelationships"
import type { AllDocumentTypes, AuthorDocumentData, Simplify } from "../../../../prismicio-types.d"

const docLanguage = (doc: FilledContentRelationshipField | Doc): string => {
  if (!doc.lang) return ""
  if (doc.lang.slice(0, 2) === "en") return ""

  return `/${doc.lang.slice(0, 2)}`
}

type Doc =
  | MakeOptional<AllDocumentTypes, "data">
  | FilledContentRelationshipField<"author", string, Simplify<AuthorDocumentData>>

export const asUrl = (doc: Doc): string => {
  const path = sitemapLinkResolver(doc) ?? "/"
  return `${docLanguage(doc)}${path}`
}

export const linkResolver: LinkResolverFunction = doc => {
  return asUrl(doc as unknown as Doc)
}

export const sitemapLinkResolver = (doc: Doc) => {
  const cityUid = (doc.data && "city" in doc.data && getValidContentRelationship(doc.data.city)?.uid) || null

  // Generic
  if (doc.type === "author") {
    return `/authors/${doc.uid}`
  }

  if (doc.type === "blog_post") {
    return `/blog/${doc.uid}`
  }

  if (doc.type === "legal") {
    return `/legal/${doc.uid}`
  }

  if (doc.type === "store") {
    return `/stores/${cityUid}/${doc.uid}`
  }

  if (doc.type === "partnership") {
    return `/partnership/${doc.uid}`
  }

  // Luggage Storage
  if (doc.type === "city") {
    return `/city/${doc.uid}`
  }

  if (doc.type === "place") {
    if (!cityUid) return null

    return `/city/${cityUid}/${doc.uid}`
  }

  if (doc.type === "event") {
    if (!cityUid) return null

    return `/events/${cityUid}/${doc.uid}`
  }

  if (doc.type === "luggage_storage_landing_page") {
    return `/ls/${doc.uid}`
  }

  if (doc.type === "homepage") {
    return `/`
  }

  // Packages
  if (doc.type === "package_acceptance_city") {
    return `/packages/${doc.uid}`
  }

  if (doc.type === "package_acceptance_usecase") {
    return `/packages/social/${doc.uid}`
  }

  if (doc.type === "package_acceptance_landing_page") {
    return `/packages/usecase/${doc.uid}`
  }

  // Partner
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (doc.type === "partnerLandingPage") {
    return `/partner/${doc.uid}`
  }

  return null
}
